import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, SkipForward, Upload } from 'lucide-react';
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';

import {
  autoMatchInvoices,
  createInvoiceLink,
  deleteInvoiceDocument,
  getInvoiceDownload,
  getInvoiceExtraction,
  getMatchSuggestions,
  listInvoicePool,
  reextractInvoice,
  saveInvoiceExtraction,
  searchTransactions,
  uploadInvoice,
  type DocumentationVerdictOut,
  type InvoiceExtractionIn,
  type MatchSuggestionOut,
  type TransactionSearchItemOut,
} from '@/api/invoices';
import { AutoMatchedPanel } from '@/components/AutoMatchedPanel';
import { FileTable } from '@/components/FileTable';
import { hasAnyInvoiceFact, InvoiceFactsFields } from '@/components/InvoiceFactsFields';
import { MissingInvoicesPanel } from '@/components/MissingInvoicesPanel';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useInvoiceQueue } from '@/hooks/useInvoiceQueue';
import { isAiPrefill, isLowConfidencePrefill } from '@/lib/aiPrefill';
import { matchReasons, suggestionIndexForKey } from '@/lib/matchSuggestions';
import { parseTransactionIds } from '@/lib/transactionIdInput';
import {
  EMPTY_FILTERS,
  hasActiveFilters,
  searchFilterParams,
  type AmountToleranceMode,
  type TransactionSearchFilters,
} from '@/lib/transactionSearchFilters';
import { cn } from '@/lib/utils';
import { useAuth } from '../auth/AuthContext';

/** The flashcard invoice workbench — the Invoices tab on the customer
 *  detail page (deep-linkable via `?tab=invoices`).
 *
 *  Label-first flow: drag in the customer's invoice PDFs, then walk
 *  the unassigned queue one document at a time — preview left, LABEL
 *  + assignment right. The label (document type + the CRA SOR/91-45
 *  facts) binds to the DOCUMENT and saves independently of any
 *  assignment; every manual label is training data for the future
 *  extractor. The picker searches ALL of the customer's transactions
 *  with context previews; multi-select covers the
 *  statement-covers-several-transactions case.
 *
 *  Keyboard: 1–9 attaches that suggestion directly, Enter assigns the
 *  selection (when at least one transaction is selected), S skips.
 *  Same keyboard-first ethos as the review queue.
 */
export function InvoiceWorkbench({ slug }: { slug: string }) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();

  const pool = useQuery({
    queryKey: ['invoice-pool', slug, 'unassigned', userKey],
    enabled: isSignedIn,
    queryFn: async () =>
      listInvoicePool(slug, (await getIdToken()) ?? '', {
        unassigned_only: true,
        limit: 500,
      }),
  });

  const documents = useMemo(() => pool.data?.items ?? [], [pool.data]);
  const queue = useInvoiceQueue(documents);
  const progress = pool.data?.progress ?? null;

  const invalidatePool = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['invoice-pool', slug] });
    // Run pages' attachment chips read this key — an assignment here
    // must surface there without a hard refresh.
    void qc.invalidateQueries({ queryKey: ['transaction-documents', slug] });
    void qc.invalidateQueries({ queryKey: ['auto-matched-links', slug] });
  }, [qc, slug]);

  return (
    <section className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h3 className="text-base font-semibold text-brand-navy">Invoice assignment</h3>
          <AutoMatchButton slug={slug} onDone={invalidatePool} />
        </div>
        {progress && (
          <p className="text-sm text-brand-slate">
            {progress.assigned_documents} of {progress.total_documents} documents assigned ·{' '}
            {progress.total_links} links
            {queue.skippedCount > 0 && ` · ${queue.skippedCount} skipped this session`}
          </p>
        )}
      </div>

      <UploadZone slug={slug} onUploaded={invalidatePool} />

      {pool.isPending ? (
        <p className="text-sm text-brand-slate">Loading invoice pool…</p>
      ) : queue.current === null ? (
        <div className="rounded-lg border border-dashed border-border bg-muted/30 px-6 py-10 text-center">
          <p className="text-sm font-medium text-brand-navy">
            {documents.length === 0
              ? 'No unassigned invoices — drop files above to start.'
              : 'Queue finished for this session.'}
          </p>
        </div>
      ) : (
        <Flashcard
          key={queue.current.id}
          slug={slug}
          document={queue.current}
          remaining={queue.remaining}
          onSkip={queue.skip}
          onAssigned={(id) => {
            queue.complete(id);
            void invalidatePool();
          }}
        />
      )}

      <AutoMatchedPanel slug={slug} />

      <MissingInvoicesPanel slug={slug} />

      <AllFilesSection slug={slug} onChanged={invalidatePool} />
    </section>
  );
}

/** Every uploaded file for this customer — practitioner and portal
 *  uploads alike — as a compact managed table: select, inspect
 *  type/size/uploader, remove one or many (confirmed). */
function AllFilesSection({ slug, onChanged }: { slug: string; onChanged: () => void }) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const allFiles = useQuery({
    queryKey: ['invoice-pool', slug, 'all-files', userKey],
    enabled: isSignedIn,
    // Aggregate every page — a single capped request would silently hide
    // rows (and their delete controls) for customers with large pools.
    queryFn: async () => {
      const token = (await getIdToken()) ?? '';
      const items = [];
      let offset: number | null = 0;
      while (offset !== null) {
        const page = await listInvoicePool(slug, token, { limit: 500, offset });
        items.push(...page.items);
        offset = page.next_offset;
      }
      return items;
    },
  });

  const rows = (allFiles.data ?? []).map((d) => ({
    id: d.id,
    filename: d.filename,
    byteCount: d.byte_count,
    uploadedByEmail: d.uploaded_by_email ?? null,
    linkCount: d.link_count,
  }));

  const handleDelete = async (ids: string[]) => {
    setDeleting(true);
    setDeleteError(null);
    try {
      const token = (await getIdToken()) ?? '';
      const failed: string[] = [];
      for (const id of ids) {
        try {
          await deleteInvoiceDocument(slug, id, token);
        } catch {
          failed.push(rows.find((r) => r.id === id)?.filename ?? id);
        }
      }
      if (failed.length) setDeleteError(`Couldn't delete: ${failed.join(', ')}`);
    } catch {
      setDeleteError("Couldn't delete: session token unavailable. Refresh and retry.");
    } finally {
      setDeleting(false);
    }
    onChanged();
  };

  return (
    <div className="grid gap-2">
      <h3 className="text-base font-semibold text-brand-navy">All files</h3>
      {allFiles.isPending ? (
        <p className="text-sm text-brand-slate">Loading files…</p>
      ) : allFiles.isError ? (
        <p className="text-sm text-destructive">Couldn't load the file list.</p>
      ) : rows.length === 0 ? (
        <p className="text-sm text-brand-slate">No files uploaded yet.</p>
      ) : (
        <FileTable rows={rows} onDelete={handleDelete} deleting={deleting} />
      )}
      {deleteError && <p className="text-sm text-destructive">{deleteError}</p>}
    </div>
  );
}

function UploadZone({ slug, onUploaded }: { slug: string; onUploaded: () => void }) {
  const { getIdToken } = useAuth();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [report, setReport] = useState<string | null>(null);
  const [failedNames, setFailedNames] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const handleFiles = useCallback(
    async (files: FileList | File[]) => {
      const list = Array.from(files);
      if (list.length === 0) return;
      setBusy(true);
      setReport(null);
      setFailedNames([]);
      let fresh = 0;
      let duplicate = 0;
      const failed: string[] = [];
      const token = (await getIdToken()) ?? '';
      for (const file of list) {
        try {
          const out = await uploadInvoice(slug, file, token);
          // The API answers 200 with the existing row for identical
          // bytes; a non-zero link_count means it was already assigned.
          if (out.link_count > 0) duplicate += 1;
          else fresh += 1;
        } catch {
          failed.push(file.name);
        }
      }
      setBusy(false);
      setReport(
        [`${fresh} uploaded`, duplicate > 0 ? `${duplicate} already assigned (skipped)` : null]
          .filter(Boolean)
          .join(' · '),
      );
      // Surface failures prominently (not just a muted count) — a
      // server-side upload error otherwise reads as a silent no-op.
      setFailedNames(failed);
      onUploaded();
    },
    [slug, getIdToken, onUploaded],
  );

  return (
    <div className="grid gap-1.5">
      <div
        role="button"
        tabIndex={0}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click();
        }}
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          void handleFiles(e.dataTransfer.files);
        }}
        className={cn(
          'flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed px-4 py-5 text-sm transition-colors',
          dragOver
            ? 'border-brand-royal bg-brand-royal/5 text-brand-navy'
            : 'border-border bg-muted/20 text-brand-slate hover:border-brand-royal/50',
        )}
      >
        <Upload className="h-4 w-4" aria-hidden="true" />
        {busy
          ? 'Uploading…'
          : (report ?? 'Drop invoice files here (PDF, images, anything) or click to browse')}
        <input
          ref={inputRef}
          type="file"
          multiple
          className="hidden"
          onChange={(e) => {
            if (e.target.files) void handleFiles(e.target.files);
            e.target.value = '';
          }}
        />
      </div>
      {failedNames.length > 0 && (
        <p
          role="alert"
          className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-xs text-destructive"
        >
          {failedNames.length} file{failedNames.length === 1 ? '' : 's'} failed to upload —
          retry: {failedNames.join(', ')}
        </p>
      )}
    </div>
  );
}

function Flashcard({
  slug,
  document: doc,
  remaining,
  onSkip,
  onAssigned,
}: {
  slug: string;
  document: { id: string; filename: string; from_customer: boolean };
  remaining: number;
  onSkip: () => void;
  onAssigned: (documentId: string) => void;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const qc = useQueryClient();

  const download = useQuery({
    queryKey: ['invoice-download', slug, doc.id, userKey],
    enabled: isSignedIn,
    queryFn: async () => getInvoiceDownload(slug, doc.id, (await getIdToken()) ?? ''),
    staleTime: 30 * 60 * 1000,
  });

  const [selected, setSelected] = useState<TransactionSearchItemOut[]>([]);
  // The document's label — editable before (and independent of) any
  // assignment. Seeded from the stored extraction when one exists.
  const [label, setLabel] = useState<InvoiceExtractionIn>({});
  const [verdict, setVerdict] = useState<DocumentationVerdictOut | null>(null);

  const extraction = useQuery({
    queryKey: ['invoice-extraction', slug, doc.id, userKey],
    enabled: isSignedIn,
    queryFn: async () => getInvoiceExtraction(slug, doc.id, (await getIdToken()) ?? ''),
  });
  useEffect(() => {
    if (extraction.data) {
      const e = extraction.data;
      // Response enums widen to string | null; the stored values came
      // from the same vocabulary the In type narrows to.
      setLabel({
        document_type: e.document_type as InvoiceExtractionIn['document_type'],
        supplier_name_on_invoice: e.supplier_name_on_invoice,
        invoice_date: e.invoice_date,
        invoice_total: e.invoice_total,
        supplier_bn: e.supplier_bn,
        tax_indication: e.tax_indication as InvoiceExtractionIn['tax_indication'],
        tax_shown: e.tax_shown,
        recipient_name: e.recipient_name,
        payment_terms: e.payment_terms,
        supply_description_quality:
          e.supply_description_quality as InvoiceExtractionIn['supply_description_quality'],
        invoice_number: e.invoice_number,
        notes: e.notes,
      });
      setVerdict(e.verdict);
    }
  }, [extraction.data]);

  const suggestions = useQuery({
    queryKey: ['match-suggestions', slug, doc.id, userKey],
    // Labelling is what enables matching — no label, no candidates.
    enabled: isSignedIn && extraction.data != null,
    queryFn: async () => getMatchSuggestions(slug, doc.id, (await getIdToken()) ?? ''),
  });

  const saveLabel = useMutation({
    mutationFn: async () =>
      saveInvoiceExtraction(slug, doc.id, label, (await getIdToken()) ?? ''),
    onSuccess: (saved) => {
      setVerdict(saved.verdict);
      void qc.invalidateQueries({ queryKey: ['invoice-extraction', slug, doc.id] });
      void qc.invalidateQueries({ queryKey: ['match-suggestions', slug, doc.id] });
      void qc.invalidateQueries({ queryKey: ['invoice-pool', slug] });
      void qc.invalidateQueries({ queryKey: ['transaction-documents', slug] });
    },
  });

  const reextract = useMutation({
    mutationFn: async () => reextractInvoice(slug, doc.id, (await getIdToken()) ?? ''),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-extraction', slug, doc.id] });
      void qc.invalidateQueries({ queryKey: ['match-suggestions', slug, doc.id] });
    },
  });

  const assign = useMutation({
    // Takes the transactions explicitly so a suggestion's one-click
    // attach doesn't round-trip through the selection state.
    mutationFn: async (txs: TransactionSearchItemOut[]) => {
      const token = (await getIdToken()) ?? '';
      // Persist the typed label before assigning. The assign call carries
      // no facts, so without this the practitioner's typed invoice facts
      // are lost whenever they assign without first clicking Save label
      // (INP-493). Idempotent PUT; skip only when the label is empty AND
      // no extraction exists yet, so a fact-less assign doesn't create a
      // blank row — but clearing an existing label still persists.
      if (hasAnyInvoiceFact(label) || extraction.data != null) {
        await saveInvoiceExtraction(slug, doc.id, label, token);
      }
      for (const tx of txs) {
        await createInvoiceLink(
          slug,
          {
            external_transaction_id: tx.external_transaction_id,
            source_document_id: doc.id,
          },
          token,
        );
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['invoice-extraction', slug, doc.id] });
      void qc.invalidateQueries({ queryKey: ['transaction-documents', slug] });
      onAssigned(doc.id);
    },
  });

  const canAssign = selected.length > 0 && !assign.isPending;
  const suggestionItems = suggestions.data?.items ?? [];

  // Enter assigns the selection / 1–5 attaches that suggestion directly /
  // S skips — suppressed while a text field owns focus (same convention
  // as the review queue).
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const idx = suggestionIndexForKey(e.key, suggestionItems.length);
      if (idx !== null && !assign.isPending) {
        e.preventDefault();
        assign.mutate([suggestionItems[idx].item]);
      } else if (e.key === 'Enter' && canAssign) {
        e.preventDefault();
        assign.mutate(selected);
      } else if (e.key.toLowerCase() === 's') {
        e.preventDefault();
        onSkip();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [canAssign, assign, onSkip, selected, suggestionItems]);

  const isImage = /\.(png|jpe?g|gif|webp|heic)$/i.test(doc.filename);
  const isPdf = /\.pdf$/i.test(doc.filename);

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
      <section className="grid content-start gap-2 rounded-lg border border-border bg-card p-3">
        <div className="flex items-baseline justify-between gap-3">
          <p className="truncate font-mono text-xs text-brand-slate">{doc.filename}</p>
          <p className="flex shrink-0 items-baseline gap-2 text-xs text-brand-slate">
            {doc.from_customer && (
              <Badge className="border-sky-500/40 bg-sky-50 px-1.5 py-0 text-[10px] text-sky-900 hover:bg-sky-50">
                from customer
              </Badge>
            )}
            {extraction.data ? (
              <Badge className="border-emerald-500/40 bg-emerald-50 px-1.5 py-0 text-[10px] text-emerald-900 hover:bg-emerald-50">
                labelled
              </Badge>
            ) : (
              <Badge variant="outline" className="px-1.5 py-0 text-[10px] font-normal">
                unlabelled
              </Badge>
            )}
            <span>{remaining} in queue</span>
          </p>
        </div>
        {download.isPending ? (
          <p className="py-16 text-center text-sm text-brand-slate">Loading preview…</p>
        ) : download.isError || !download.data ? (
          <p className="py-16 text-center text-sm text-brand-slate">Preview unavailable.</p>
        ) : isPdf ? (
          <iframe
            title={doc.filename}
            src={download.data.url}
            className="h-[70vh] w-full rounded-md border border-border bg-white"
          />
        ) : isImage ? (
          <img
            src={download.data.url}
            alt={doc.filename}
            className="max-h-[70vh] w-full rounded-md border border-border object-contain"
          />
        ) : (
          <p className="py-16 text-center text-sm">
            <a
              href={download.data.url}
              target="_blank"
              rel="noreferrer"
              className="text-brand-royal underline underline-offset-4"
            >
              Download {doc.filename}
            </a>{' '}
            <span className="text-brand-slate">(no inline preview for this type)</span>
          </p>
        )}
      </section>

      <section className="grid content-start gap-4">
        <LabelForm
          label={label}
          onChange={setLabel}
          verdict={verdict}
          onSave={() => saveLabel.mutate()}
          saving={saveLabel.isPending}
          saveError={saveLabel.isError}
          extractedBy={extraction.data?.extracted_by ?? null}
          confidence={extraction.data?.confidence ?? null}
          onReextract={() => reextract.mutate()}
          reextracting={reextract.isPending}
        />
        {extraction.data != null && (
          <MatchSuggestions
            items={suggestionItems}
            loading={suggestions.isPending}
            autoPick={suggestions.data?.auto_pick ?? null}
            selected={selected}
            attaching={assign.isPending}
            onAttach={(item) => assign.mutate([item])}
            onToggle={(item) => {
              const exists = selected.some(
                (s) => s.external_transaction_id === item.external_transaction_id,
              );
              setSelected(
                exists
                  ? selected.filter(
                      (s) => s.external_transaction_id !== item.external_transaction_id,
                    )
                  : [...selected, item],
              );
            }}
          />
        )}
        <TransactionPicker slug={slug} selected={selected} onChange={setSelected} />
        <div className="flex items-center gap-2">
          <Button type="button" disabled={!canAssign} onClick={() => assign.mutate(selected)}>
            <Check className="mr-1 h-4 w-4" aria-hidden="true" />
            {assign.isPending
              ? 'Assigning…'
              : `Assign to ${selected.length || '…'} transaction${selected.length === 1 ? '' : 's'}`}
          </Button>
          <Button type="button" variant="outline" onClick={onSkip}>
            <SkipForward className="mr-1 h-4 w-4" aria-hidden="true" />
            Skip
          </Button>
          <p className="text-xs text-brand-slate">
            {suggestionItems.length > 0 && `1–${Math.min(suggestionItems.length, 9)} attaches a suggestion · `}
            Enter assigns · S skips
          </p>
        </div>
        {assign.isError && (
          <p className="text-sm text-red-700">Assignment failed — retry or skip.</p>
        )}
      </section>
    </div>
  );
}

const STATUS_BADGE: Record<string, string> = {
  approved: 'bg-emerald-100 text-emerald-800',
  rejected: 'bg-red-100 text-red-800',
  needs_review: 'bg-amber-100 text-amber-800',
  pending: 'bg-slate-100 text-slate-700',
};

/** Minimal selection entry for a hand-typed id — no ledger context
 *  until (unless) the enrichment lookup finds the transaction. Links
 *  key on the external id string, so an id absent from the
 *  transactions table still assigns correctly. */
function stubItem(id: string): TransactionSearchItemOut {
  return {
    external_transaction_id: id,
    transaction_type: null,
    supplier_name: null,
    bill_date: null,
    bill_amount: '0',
    tax_amount: '0',
    currency: 'CAD',
    findings: [],
    link_count: 0,
  };
}

function TransactionPicker({
  slug,
  selected,
  onChange,
}: {
  slug: string;
  selected: TransactionSearchItemOut[];
  onChange: (next: TransactionSearchItemOut[]) => void;
}) {
  const { isSignedIn, userKey, getIdToken } = useAuth();
  const [idEntry, setIdEntry] = useState('');
  const [q, setQ] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<TransactionSearchFilters>(EMPTY_FILTERS);
  const deferredQ = useDeferredValue(q);
  const filterParams = searchFilterParams(filters);
  const filtersActive = hasActiveFilters(filters);
  // Latest selection for the async enrichment below — the prop value
  // in that closure goes stale the moment the stubs are added.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  const addByIds = async () => {
    const ids = parseTransactionIds(
      idEntry,
      selected.map((s) => s.external_transaction_id),
    );
    if (ids.length === 0) return;
    setIdEntry('');
    onChange([...selected, ...ids.map(stubItem)]);
    // Best-effort enrichment: when the ledger knows the id, swap the
    // stub for the real context preview. A miss changes nothing — the
    // link still keys on the id string.
    const token = (await getIdToken()) ?? '';
    for (const id of ids) {
      try {
        // Substring search can match many ids ("18" is a prefix of
        // thousands) — a generous page keeps the exact id in reach.
        const found = await searchTransactions(slug, token, { q: id, limit: 50 });
        const exact = found.items.find((i) => i.external_transaction_id === id);
        if (exact) {
          onChange(
            selectedRef.current.map((s) =>
              s.external_transaction_id === id ? exact : s,
            ),
          );
        }
      } catch {
        // Lookup failures never block a by-id assignment.
      }
    }
  };

  const search = useQuery({
    queryKey: ['transaction-search', slug, deferredQ, filterParams, userKey],
    enabled: isSignedIn && (deferredQ.trim().length > 0 || filtersActive),
    queryFn: async () =>
      searchTransactions(slug, (await getIdToken()) ?? '', {
        q: deferredQ.trim() || undefined,
        ...filterParams,
      }),
  });

  const toggle = (item: TransactionSearchItemOut) => {
    const exists = selected.some(
      (s) => s.external_transaction_id === item.external_transaction_id,
    );
    onChange(
      exists
        ? selected.filter((s) => s.external_transaction_id !== item.external_transaction_id)
        : [...selected, item],
    );
  };

  return (
    <div className="grid gap-2 rounded-lg border border-border bg-card p-3">
      <label className="grid gap-1 text-sm">
        <span className="text-xs font-medium uppercase tracking-wide text-brand-slate">
          Assign by transaction ID
        </span>
        <div className="flex gap-1.5">
          <input
            value={idEntry}
            onChange={(e) => setIdEntry(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                void addByIds();
              }
            }}
            placeholder="183755 — paste one or many, comma-separated"
            className="min-w-0 flex-1 rounded-md border border-input bg-background px-2.5 py-1.5 font-mono text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
          />
          <Button
            type="button"
            size="sm"
            variant="outline"
            disabled={idEntry.trim().length === 0}
            onClick={() => void addByIds()}
          >
            Add
          </Button>
        </div>
        <span className="text-[11px] text-brand-slate">
          IDs link directly — they don&apos;t need to appear in search.
        </span>
      </label>

      <label className="grid gap-1 text-sm">
        <span className="text-xs font-medium uppercase tracking-wide text-brand-slate">
          Or search
        </span>
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search by supplier, transaction id…"
          className="rounded-md border border-input bg-background px-2.5 py-1.5 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
        />
      </label>

      <div className="grid gap-1.5">
        <button
          type="button"
          onClick={() => setShowFilters((v) => !v)}
          className="justify-self-start text-xs font-medium text-brand-slate hover:text-brand-navy"
        >
          {showFilters ? '▾' : '▸'} Filters
          {filtersActive && <span className="ml-1 text-brand-royal">•</span>}
        </button>
        {showFilters && (
          <div className="grid gap-1.5 rounded-md border border-border p-2 sm:grid-cols-2">
            <label className="grid gap-0.5 text-xs text-brand-slate">
              Amount
              <div className="flex gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  value={filters.amount}
                  onChange={(e) => setFilters((f) => ({ ...f, amount: e.target.value }))}
                  placeholder="1130.00"
                  className="min-w-0 flex-1 rounded-md border border-input bg-background px-2 py-1 font-mono text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
                />
                <select
                  value={filters.toleranceMode}
                  onChange={(e) =>
                    setFilters((f) => ({
                      ...f,
                      toleranceMode: e.target.value as AmountToleranceMode,
                    }))
                  }
                  className="rounded-md border border-input bg-background px-1.5 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
                >
                  <option value="exact">exact</option>
                  <option value="dollar">±$1</option>
                  <option value="pct2">±2%</option>
                </select>
              </div>
            </label>
            <label className="grid gap-0.5 text-xs text-brand-slate">
              GL account
              <input
                type="text"
                value={filters.glAccount}
                onChange={(e) => setFilters((f) => ({ ...f, glAccount: e.target.value }))}
                placeholder="7205 or software"
                className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
            </label>
            <label className="grid gap-0.5 text-xs text-brand-slate">
              From
              <input
                type="date"
                value={filters.dateFrom}
                onChange={(e) => setFilters((f) => ({ ...f, dateFrom: e.target.value }))}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
            </label>
            <label className="grid gap-0.5 text-xs text-brand-slate">
              To
              <input
                type="date"
                value={filters.dateTo}
                onChange={(e) => setFilters((f) => ({ ...f, dateTo: e.target.value }))}
                className="rounded-md border border-input bg-background px-2 py-1 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal"
              />
            </label>
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {selected.map((s) => (
            <button
              key={s.external_transaction_id}
              type="button"
              onClick={() => toggle(s)}
              title="Click to remove"
              className="inline-flex items-center gap-1 rounded-full bg-brand-royal/10 px-2 py-0.5 text-xs font-medium text-brand-navy hover:bg-brand-royal/20"
            >
              {s.external_transaction_id}
              <span aria-hidden>×</span>
            </button>
          ))}
        </div>
      )}

      {search.isPending && (deferredQ.trim().length > 0 || filtersActive) && (
        <p className="text-xs text-brand-slate">Searching…</p>
      )}
      {search.data && search.data.items.length === 0 && (
        <p className="text-xs text-brand-slate">No transactions match.</p>
      )}
      {search.data && search.data.items.length > 0 && (
        <ul className="grid max-h-72 gap-1 overflow-y-auto">
          {search.data.items.map((item) => {
            const isSelected = selected.some(
              (s) => s.external_transaction_id === item.external_transaction_id,
            );
            return (
              <li key={item.external_transaction_id}>
                <button
                  type="button"
                  onClick={() => toggle(item)}
                  className={cn(
                    'grid w-full gap-0.5 rounded-md border px-2.5 py-1.5 text-left text-sm transition-colors',
                    isSelected
                      ? 'border-brand-royal bg-brand-royal/5'
                      : 'border-border hover:border-brand-royal/50',
                  )}
                >
                  <span className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-brand-navy">
                      {item.supplier_name ?? '(no supplier)'}
                    </span>
                    <span className="font-mono text-xs text-brand-slate">
                      {item.external_transaction_id}
                    </span>
                  </span>
                  <span className="flex flex-wrap items-baseline gap-2 text-xs text-brand-slate">
                    <span className="font-mono">
                      {item.bill_amount} {item.currency}
                    </span>
                    <span>tax {item.tax_amount}</span>
                    {item.bill_date && <span>{item.bill_date}</span>}
                    {item.transaction_type && <span>{item.transaction_type}</span>}
                    {item.link_count > 0 && <span>📎 {item.link_count}</span>}
                  </span>
                  {item.findings.length > 0 && (
                    <span className="flex flex-wrap gap-1 pt-0.5">
                      {item.findings.map((f) => (
                        <Badge
                          key={f.finding_id}
                          className={cn(
                            'px-1.5 py-0 text-[10px]',
                            STATUS_BADGE[f.status] ?? STATUS_BADGE.pending,
                          )}
                        >
                          {f.rule_id} · {f.status}
                        </Badge>
                      ))}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export const FIELD_LABELS: Record<string, string> = {
  supplier_name_on_invoice: 'supplier name',
  invoice_date: 'date',
  invoice_total: 'total',
  supplier_bn: 'BN',
  tax_indication: 'tax indication',
  recipient_name: 'recipient',
  payment_terms: 'payment terms',
  supply_description_quality: 'supply description',
};

/** Human-readable list of SOR/91-45 fields, for the verdict bar. */
export function describeFields(fields: string[]): string {
  return fields.map((f) => FIELD_LABELS[f] ?? f).join(', ');
}

function VerdictBar({ verdict }: { verdict: DocumentationVerdictOut }) {
  if (!verdict.supportable) {
    return (
      <p className="rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive">
        This document type cannot support an ITC claim — chase the final invoice.
      </p>
    );
  }
  return (
    <div className="grid gap-1">
      <p
        className={cn(
          'rounded-md border px-2.5 py-1.5 text-xs',
          verdict.complete
            ? 'border-emerald-500/40 bg-emerald-50 text-emerald-900'
            : 'border-amber-500/50 bg-amber-50 text-amber-900',
        )}
      >
        {verdict.tier !== null ? `Tier ${verdict.tier}` : 'Tier unknown (no total) — tier 3 checklist'}
        {verdict.complete
          ? ' · documentation complete ✓'
          : ` · missing: ${describeFields(verdict.missing_fields)}`}
        {verdict.weak_fields.length > 0 && ` · weak: ${describeFields(verdict.weak_fields)}`}
      </p>
      {verdict.simplified_registry_bn && (
        <p className="rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-1.5 text-xs text-destructive">
          RT9999 — simplified-registry supplier; ITC blocked (s.211.13) regardless of documentation.
        </p>
      )}
    </div>
  );
}

function LabelForm({
  label,
  onChange,
  verdict,
  onSave,
  saving,
  saveError,
  extractedBy,
  confidence,
  onReextract,
  reextracting,
}: {
  label: InvoiceExtractionIn;
  onChange: (next: InvoiceExtractionIn) => void;
  verdict: DocumentationVerdictOut | null;
  onSave: () => void;
  saving: boolean;
  saveError: boolean;
  extractedBy: string | null;
  confidence: string | null;
  onReextract: () => void;
  reextracting: boolean;
}) {
  const aiPrefill = isAiPrefill(extractedBy);
  const lowConfidence = isLowConfidencePrefill(extractedBy, confidence);
  return (
    <div className="grid gap-2 rounded-lg border border-border bg-card p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-brand-slate">
          Label this document
        </p>
        {aiPrefill && (
          <Badge className="border-violet-500/40 bg-violet-50 px-1.5 py-0 text-[10px] text-violet-900 hover:bg-violet-50">
            AI-extracted — confirm
          </Badge>
        )}
      </div>
      {aiPrefill && lowConfidence && (
        <p className="text-xs text-amber-700">
          Low confidence — double-check the fields before saving.
        </p>
      )}
      <InvoiceFactsFields value={label} onChange={onChange} />
      <div className="flex items-center gap-2">
        <Button type="button" size="sm" variant="outline" disabled={saving} onClick={onSave}>
          {saving ? 'Saving…' : aiPrefill ? 'Confirm label' : 'Save label'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          disabled={reextracting}
          onClick={onReextract}
        >
          {reextracting ? 'Re-extracting…' : 'Re-extract'}
        </Button>
        <p className="text-xs text-brand-slate">Saves to the document — assignment is separate.</p>
      </div>
      {saveError && <p className="text-sm text-red-700">Label save failed — retry.</p>}
      {verdict && <VerdictBar verdict={verdict} />}
    </div>
  );
}

/** Ranked matcher candidates for the labelled document — the primary
 *  attach path. Attach links the candidate immediately (and advances);
 *  clicking the row body toggles it into the manual selection for the
 *  statement-covers-many case. */
function MatchSuggestions({
  items,
  loading,
  autoPick,
  selected,
  attaching,
  onAttach,
  onToggle,
}: {
  items: MatchSuggestionOut[];
  loading: boolean;
  autoPick: string | null;
  selected: TransactionSearchItemOut[];
  attaching: boolean;
  onAttach: (item: TransactionSearchItemOut) => void;
  onToggle: (item: TransactionSearchItemOut) => void;
}) {
  return (
    <div className="grid gap-2 rounded-lg border border-brand-royal/30 bg-brand-royal/5 p-3">
      <p className="text-xs font-medium uppercase tracking-wide text-brand-slate">
        Suggested matches
      </p>
      {loading ? (
        <p className="text-xs text-brand-slate">Finding matches…</p>
      ) : items.length === 0 ? (
        <p className="text-xs text-brand-slate">
          No confident matches for these facts — search below or assign by ID.
        </p>
      ) : (
        <ul className="grid gap-1">
          {items.map((s, index) => {
            const isSelected = selected.some(
              (sel) => sel.external_transaction_id === s.item.external_transaction_id,
            );
            const reasons = matchReasons(s);
            return (
              <li key={s.item.external_transaction_id} className="flex items-stretch gap-1.5">
                <button
                  type="button"
                  onClick={() => onToggle(s.item)}
                  title="Click to add to the selection (multi-select)"
                  className={cn(
                    'grid min-w-0 flex-1 gap-0.5 rounded-md border bg-card px-2.5 py-1.5 text-left text-sm transition-colors',
                    isSelected
                      ? 'border-brand-royal bg-brand-royal/5'
                      : 'border-border hover:border-brand-royal/50',
                  )}
                >
                  <span className="flex flex-wrap items-baseline justify-between gap-2">
                    <span className="font-medium text-brand-navy">
                      {s.item.supplier_name ?? '(no supplier)'}
                    </span>
                    <span className="flex items-center gap-1.5">
                      {s.item.external_transaction_id === autoPick && (
                        <Badge className="border-emerald-500/40 bg-emerald-50 px-1.5 py-0 text-[10px] text-emerald-900 hover:bg-emerald-50">
                          auto ✓
                        </Badge>
                      )}
                      <span className="font-mono text-xs text-brand-slate">
                        {Math.round(s.score * 100)}%
                      </span>
                    </span>
                  </span>
                  <span className="flex flex-wrap items-baseline gap-2 text-xs text-brand-slate">
                    <span className="font-mono">
                      {s.item.bill_amount} {s.item.currency}
                    </span>
                    {s.item.bill_date && <span>{s.item.bill_date}</span>}
                    <span className="font-mono">{s.item.external_transaction_id}</span>
                  </span>
                  {reasons.length > 0 && (
                    <span className="text-xs text-brand-navy/80">{reasons.join(' · ')}</span>
                  )}
                </button>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  disabled={attaching}
                  onClick={() => onAttach(s.item)}
                  title={`Attach and advance (key ${index + 1})`}
                  className="h-auto shrink-0 border-brand-royal/40 text-brand-navy"
                >
                  <Check className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                  {index < 9 ? `${index + 1}` : ''} Attach
                </Button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

/** Batch auto-assign over every labelled unassigned invoice. The gate
 *  is conservative (exact amount + tight date + strong name, no
 *  runner-up); everything else stays in the queue for the human. */
function AutoMatchButton({ slug, onDone }: { slug: string; onDone: () => void }) {
  const { getIdToken } = useAuth();
  const [report, setReport] = useState<string | null>(null);

  const run = useMutation({
    mutationFn: async () => autoMatchInvoices(slug, (await getIdToken()) ?? ''),
    onSuccess: (result) => {
      setReport(
        `${result.assigned.length} auto-assigned · ${result.skipped} left for review`,
      );
      onDone();
    },
  });

  return (
    <span className="flex items-center gap-2">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={run.isPending}
        onClick={() => run.mutate()}
        title="Link every labelled invoice whose facts pick exactly one high-confidence transaction; links carry auto-matcher provenance and unlink normally"
      >
        {run.isPending ? 'Matching…' : 'Auto-match labelled'}
      </Button>
      {report && <span className="text-xs text-brand-slate">{report}</span>}
      {run.isError && <span className="text-xs text-red-700">Auto-match failed.</span>}
    </span>
  );
}
